import React from "react";
import Dropdown from "../Dropdown/Dropdown";
import PropTypes from "prop-types";
import filter from "../../filter";
import { executeBackendQuery } from "../../requests/executeBackendQuery";
import { getData } from "../../requests/CRUD";
import { BUYERS_LIST, BUYERS_LIST_COUNT, INVESTOR_LIST_COUNT, INVESTOR_LIST } from "../../consts/queries";
import { BUYERS, INVESTORS } from "../../consts/tables";
import { formatName } from "../../utils";
import { investorPickerSearchOrder } from "../../consts/consts";

const cloneDeep = require("lodash.clonedeep");
const limit = 10;
const debounceMs = 250;

class SelectBuyer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            currentOffset: 0,
            hasMore: true,
            isLoading: false,
            searchTerm: "",
            inputValue: "",
            selectedOptions: [],
        };

        this.debounceTimer = null;
        this.activeRequestId = 0;

        this.fetchPage = this.fetchPage.bind(this);
        this.startSearch = this.startSearch.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSelectedOptionsChange = this.handleSelectedOptionsChange.bind(this);
        this.syncSelectedOptionsFromValue = this.syncSelectedOptionsFromValue.bind(this);
        this.ensureSelectedOption = this.ensureSelectedOption.bind(this);
        this.getOptionLabel = this.getOptionLabel.bind(this);
    }

    componentDidMount() {
        this.startSearch("");
        this.ensureSelectedOption(this.props.value);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.newBuyer !== prevProps.newBuyer && this.props.newBuyer) {
            const items = cloneDeep(this.state.items);
            if (!this.investorIsDeleted(this.props.newBuyer)) {
                items.push(this.props.newBuyer);
            }
            this.setState({ items }, this.syncSelectedOptionsFromValue);
        }

        if (prevProps.value !== this.props.value) {
            this.ensureSelectedOption(this.props.value);
        }

        if (prevProps.value !== this.props.value || prevState.items !== this.state.items) {
            this.syncSelectedOptionsFromValue();
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        this.activeRequestId++;
    }

    investorIsDeleted(item) {
        if (!item || typeof item !== "object") return false;
        const d = item.DELETED;
        if (d === undefined || d === null || d === "") return false;
        if (d === 1 || d === true || d === "1") return true;
        const n = Number(d);
        return !Number.isNaN(n) && n === 1;
    }

    extractId(option) {
        if (!option) return null;
        if (typeof option === 'object') {
            if (option.ARCIMEDES_ROW_ID !== undefined) return String(option.ARCIMEDES_ROW_ID);
            if (option.value !== undefined) return String(option.value);
        }
        return option !== undefined && option !== null ? String(option) : null;
    }

    getSelectedIdsFromValue() {
        const { value } = this.props;
        if (value === null || value === undefined) return [];
        if (Array.isArray(value)) return value.map((v) => String(this.extractId(v))).filter(Boolean);
        if (typeof value === 'object') {
            const id = this.extractId(value);
            return id ? [id] : [];
        }
        return String(value).split(',').map(v => v.trim()).filter(Boolean);
    }

    syncSelectedOptionsFromValue() {
        const selectedIds = this.getSelectedIdsFromValue();
        if (!selectedIds.length) {
            if (this.state.selectedOptions.length) {
                this.setState({ selectedOptions: [] });
            }
            return;
        }

        const map = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        this.state.items.forEach(opt => {
            const id = this.extractId(opt);
            if (id && selectedIds.includes(id)) {
                map.set(id, opt);
            }
        });

        const next = selectedIds.map(id => map.get(id)).filter(Boolean);
        const nextState = { selectedOptions: next };
        if (!this.state.searchTerm && next.length === 1) {
            const label = this.getOptionLabel(next[0]);
            if (label && this.state.inputValue !== label) {
                nextState.inputValue = label;
            }
        }
        this.setState(nextState);
    }

    ensureSelectedOption(value) {
        const id = this.extractId(value);
        if (!id) return;

        const alreadyThere = this.state.items.find(item => this.extractId(item) === id);
        if (alreadyThere) return;

        getData({
            tableCode: INVESTORS,
            options: {
                limit: { count: 1, offset: 0 },
                column: [
                    "ARCIMEDES_ROW_ID",
                    "NAME",
                    "EMAIL",
                    "PHONE",
                ],
                filter: [
                    {
                        code: "ARCIMEDES_ROW_ID",
                        text: id,
                        type: "equal",
                    }
                ],
                filterType: "and",
            },
        })
            .then((res) => {
                if (res && res.success && Array.isArray(res.data) && res.data.length) {
                    this.setState((prev) => ({
                        items: [res.data[0], ...prev.items],
                    }), this.syncSelectedOptionsFromValue);
                }
            })
            .catch(() => {});
    }

    fetchPage({ offset, searchTerm, replace }) {
        const requestId = ++this.activeRequestId;
        const trimmedTerm = (searchTerm || "").trim();
        const searchPattern = trimmedTerm ? `%${trimmedTerm}%` : "";

        this.setState({ isLoading: true });

        if (!trimmedTerm) {
            const options = filter({NAME: ""}, "investors");
            options.param.limit = limit;
            options.param.offset = offset;
            options.param.NAME = "";

            return Promise.all([
                executeBackendQuery({ queryName: INVESTOR_LIST, options }),
                executeBackendQuery({ queryName: INVESTOR_LIST_COUNT, options }),
            ])
                .then((responses) => {
                    if (requestId !== this.activeRequestId) return;
                    const listRes = responses && responses[0] ? responses[0] : null;
                    const countRes = responses && responses[1] ? responses[1] : null;
                    if (listRes && listRes.success && Array.isArray(listRes.data)) {
                        const newItems = listRes.data.filter(item => item && item.NAME !== "" && !this.investorIsDeleted(item));
                        const totalCount = countRes && countRes.success && countRes.data && countRes.data[0]
                            ? Number(countRes.data[0].TOTAL_COUNT)
                            : null;
                        this.setState((prev) => ({
                            items: replace ? newItems : [...prev.items, ...newItems],
                            currentOffset: offset + newItems.length,
                            hasMore: totalCount === null ? (newItems.length === limit) : (offset + newItems.length < totalCount),
                            isLoading: false,
                            searchTerm,
                        }));
                    } else {
                        this.setState({ isLoading: false, hasMore: false });
                    }
                })
                .catch((err) => {
                    if (requestId !== this.activeRequestId) return;
                    console.error("Error fetching buyers:", err);
                    this.setState({ isLoading: false });
                });
        }

        const fetchLimit = 15;
        const optsName = filter({NAME: trimmedTerm, order: investorPickerSearchOrder}, "investors");
        optsName.param.limit = fetchLimit;
        optsName.param.offset = offset;
        optsName.param.NAME = searchPattern;

        const optsEmail = filter({EMAIL: trimmedTerm, order: investorPickerSearchOrder}, "investors");
        optsEmail.param.limit = fetchLimit;
        optsEmail.param.offset = offset;
        optsEmail.param.EMAIL = searchPattern;

        const optsPhone = filter({PHONE: trimmedTerm, order: investorPickerSearchOrder}, "investors");
        optsPhone.param.limit = fetchLimit;
        optsPhone.param.offset = offset;
        optsPhone.param.PHONE = searchPattern;

        return Promise.all([
            executeBackendQuery({ queryName: INVESTOR_LIST, options: optsName }),
            executeBackendQuery({ queryName: INVESTOR_LIST, options: optsEmail }),
            executeBackendQuery({ queryName: INVESTOR_LIST, options: optsPhone }),
        ])
            .then((responses) => {
                if (requestId !== this.activeRequestId) return;
                const pages = responses.map((res) => (res && res.success && Array.isArray(res.data) ? res.data : []));

                this.setState((prev) => {
                    const kept = replace ? [] : prev.items;
                    const seen = new Set(kept.map((item) => String(item.ARCIMEDES_ROW_ID)));
                    const merged = [...kept];
                    for (const item of pages.flat()) {
                        if (item && item.NAME !== "" && !seen.has(String(item.ARCIMEDES_ROW_ID)) && !this.investorIsDeleted(item)) {
                            seen.add(String(item.ARCIMEDES_ROW_ID));
                            merged.push(item);
                        }
                    }

                    return {
                        items: merged,
                        // Name, email and phone each page on their own; the
                        // offset steps all three until none has more.
                        currentOffset: offset + fetchLimit,
                        hasMore: pages.some((page) => page.length === fetchLimit),
                        isLoading: false,
                        searchTerm,
                    };
                });
            })
            .catch((err) => {
                if (requestId !== this.activeRequestId) return;
                console.error("Error fetching buyers:", err);
                this.setState({ isLoading: false });
            });
    }

    startSearch(searchTerm) {
        this.setState({
            items: [],
            currentOffset: 0,
            hasMore: true,
            searchTerm,
        });
        return this.fetchPage({ offset: 0, searchTerm, replace: true });
    }

    handleInputChange(event, newValue, reason) {
        if (reason === "reset") return;
        if (reason === "clear") {
            this.setState({ inputValue: "" });
            this.startSearch("");
            return;
        }

        const next = (newValue || "").trimStart();
        this.setState({ inputValue: next });

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.startSearch(next);
        }, debounceMs);
    }

    handleLoadMore() {
        if (!this.state.isLoading && this.state.hasMore) {
            this.fetchPage({
                offset: this.state.currentOffset,
                searchTerm: this.state.searchTerm,
                replace: false,
            });
        }
    }

    getOptionLabel(option) {
        if (!option || typeof option !== 'object') return '';
        return formatName(option) || '';
    }

    handleSelectedOptionsChange(options) {
        const nextOptions = Array.isArray(options) ? options.filter(Boolean) : (options ? [options] : []);
        const map = new Map();
        nextOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) map.set(id, opt);
        });
        const selectedOptions = Array.from(map.values());
        const nextState = { selectedOptions };
        if (selectedOptions.length === 1) {
            const label = this.getOptionLabel(selectedOptions[0]);
            if (label) {
                nextState.inputValue = label;
                nextState.searchTerm = "";
            }
        }
        if (selectedOptions.length === 0) {
            nextState.inputValue = "";
            nextState.searchTerm = "";
        }
        this.setState(nextState);
    }

    render() {
        const { items, hasMore, isLoading, inputValue } = this.state;
        const {
            theme,
            value,
            valueKey,
            label,
            disabled,
            data,
            withAddNew,
            hasDrawer,
            handleParentChange,
            addNewItem,
            deleted,
            viewMode,
            ...rest
        } = this.props;

        const mergedOptionsMap = new Map();
        this.state.selectedOptions.forEach(opt => {
            const id = this.extractId(opt);
            if (id) mergedOptionsMap.set(id, opt);
        });
        items.forEach(opt => {
            const id = this.extractId(opt);
            if (id && !mergedOptionsMap.has(id) && !this.investorIsDeleted(opt)) mergedOptionsMap.set(id, opt);
        });
        const mergedOptions = Array.from(mergedOptionsMap.values()).filter(opt => !this.investorIsDeleted(opt));

        return (
            <Dropdown
                theme={theme}
                optionsType="buyer"
                optionKey="ARCIMEDES_ROW_ID"
                options={mergedOptions}
                value={value}
                valueKey={valueKey}
                label={label}
                disabled={disabled}
                data={data}
                withAddNew={withAddNew}
                hasDrawer={hasDrawer}
                handleParentChange={handleParentChange}
                addNewItem={addNewItem}
                deleted={deleted}
                viewMode={viewMode}
                disableClientFilter={true}
                onInputChange={this.handleInputChange}
                inputValue={inputValue}
                loading={isLoading}
                loadingText="Searching..."
                noOptionsText={inputValue ? "No matching buyers" : "Type to search buyers"}
                onSelectedOptionsChange={this.handleSelectedOptionsChange}
                ListboxProps={{
                    onScroll: (e) => {
                        const { target } = e;
                        if (target.scrollTop + target.clientHeight >= target.scrollHeight * 0.8) {
                            if (hasMore && !isLoading) {
                                this.handleLoadMore();
                            }
                        }
                    }
                }}
                {...rest}
            />
        );
    }
}

SelectBuyer.propTypes = {
    theme: PropTypes.oneOf(["default", "white", "dark"]),
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    data: PropTypes.object,
    withAddNew: PropTypes.bool,
    handleParentChange: PropTypes.func.isRequired,
    viewMode: PropTypes.bool,
    hasDrawer: PropTypes.bool,
    addNewItem: PropTypes.func,
    deleted: PropTypes.bool,
    isFromProperty: PropTypes.bool,
};

export default SelectBuyer;
